// INVDESIGN1-S1 (docs/build-briefs/INVDESIGN1-S1-count-flow.md; owner-approved mockups invdesign1-3 and invdesign1-4).
// Pure, no DOM: the area-by-area count's tabs, walking order, tenths and keg fractions, provable in a plain node:test
// file (test/invdesign1-s1-count-area-model.test.ts). An area is a count section; the server already returns the
// sections in walking order and the items in section-then-shelf order, and this file only groups and reads them.

import {
  UNASSIGNED_TAB,
  formatHundredths,
  tabIdForRow,
  totalHundredths,
  type TableEntry,
  type TableRow,
} from "./countTableModel.ts";

export interface AreaTab {
  id: string;
  label: string;
  counted: number;
  total: number;
  /** Every item counted (and there is at least one). */
  done: boolean;
}

/** The areas in walking order, each with its tally. Unassigned comes last and only when it holds an item. An area with
 * no item is still a tab: the counter can see it exists and that nothing in it needs counting. */
export function areaTabs(
  rows: readonly TableRow[],
  sections: readonly { id: string; name: string }[],
): AreaTab[] {
  const tally = (id: string, label: string): AreaTab => {
    const inArea = rows.filter((row) => tabIdForRow(row) === id);
    const counted = inArea.filter((row) => row.entry !== null).length;
    return {
      id,
      label,
      counted,
      total: inArea.length,
      done: inArea.length > 0 && counted === inArea.length,
    };
  };
  const tabs = sections.map((section) => tally(section.id, section.name));
  const unassigned = tally(UNASSIGNED_TAB, "Unassigned");
  return unassigned.total > 0 ? [...tabs, unassigned] : tabs;
}

/** The area to open on: the first one in walking order that still has something to count, else the first area. */
export function firstOpenArea(tabs: readonly AreaTab[]): string | undefined {
  return (tabs.find((tab) => tab.total > 0 && !tab.done) ?? tabs[0])?.id;
}

export function areaRows(rows: readonly TableRow[], areaId: string): TableRow[] {
  return rows.filter((row) => tabIdForRow(row) === areaId);
}

/** The item to make active after `currentId`: the next uncounted one further down the area, else the first uncounted
 * one above it, else undefined (the area is finished). With no current item, the first uncounted one. */
export function nextUncountedId(
  rowsInArea: readonly TableRow[],
  currentId: string | undefined,
): string | undefined {
  const start = currentId === undefined ? -1 : rowsInArea.findIndex((row) => row.id === currentId);
  const after = rowsInArea.slice(start + 1).find((row) => row.entry === null);
  if (after !== undefined) return after.id;
  return rowsInArea.slice(0, Math.max(start, 0)).find((row) => row.entry === null)?.id;
}

export function previousAreaId(tabs: readonly AreaTab[], areaId: string): string | undefined {
  const index = tabs.findIndex((tab) => tab.id === areaId);
  return index > 0 ? tabs[index - 1]?.id : undefined;
}

export function followingAreaId(tabs: readonly AreaTab[], areaId: string): string | undefined {
  const index = tabs.findIndex((tab) => tab.id === areaId);
  return index >= 0 ? tabs[index + 1]?.id : undefined;
}

// ---------------------------------------------------------------------
// Bottles: whole bottles and tenths
// ---------------------------------------------------------------------

/** The part-bottle buttons: Empty (no open bottle part) then .1 to .9. */
export const AREA_TENTHS: readonly number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export function tenthsLabel(tenths: number): string {
  return tenths === 0 ? "Empty" : `.${tenths}`;
}

/** "1.1": the whole bottles and the tenth together, as the Counting figure reads. */
export function countingFigure(entry: TableEntry | null, caseSize: number | null): string {
  return entry === null ? "-" : formatHundredths(totalHundredths(entry, caseSize));
}

// ---------------------------------------------------------------------
// Kegs: a fraction left on tap plus full spares
// ---------------------------------------------------------------------

/** Quarters of a keg left on tap: 0 Empty, 1 for 1/4, 2 for 1/2, 3 for 3/4, 4 Full. */
export const KEG_TAP_QUARTERS: readonly number[] = [0, 1, 2, 3, 4];

const KEG_TAP_LABELS: readonly string[] = ["Empty", "1/4", "1/2", "3/4", "Full"];

export function kegTapLabel(quarters: number): string {
  return KEG_TAP_LABELS[quarters] ?? "Empty";
}

export const MAX_KEG_SPARES = 99;

/** What the counter has said: quarters on tap (0 to 4) and whole full spares. */
export interface KegCount {
  tapQuarters: number;
  spares: number;
}

/** The keg count as a stored entry: kegs = on tap + spares, in exact hundredths (a quarter is .25). */
export function kegEntry(count: KegCount): TableEntry {
  const total = (count.tapQuarters + count.spares * 4) * 25;
  const whole = Math.floor(total / 100);
  const rest = total % 100;
  const entry: TableEntry = { cases: 0, bottles: whole, tenths: Math.floor(rest / 10) };
  return rest % 10 === 0 ? entry : { ...entry, hundredths: rest % 10 };
}

/** The keg count as kegs in two decimals, trimmed: 1/4 on tap and 1 spare is "1.25". */
export function kegQuantity(count: KegCount): string {
  return formatHundredths((count.tapQuarters + count.spares * 4) * 25);
}

/** Reads a saved entry back as a keg count (a count made earlier, or on another screen). The split is not stored, so a
 * whole number of kegs reads as Full on tap plus the rest as spares; the total is what matters and it is exact. A figure
 * that is not a quarter (a table entry of 1.3) rounds to the nearest quarter for display only; nothing is rewritten. */
export function kegCountFromEntry(entry: TableEntry): KegCount {
  const quarters = Math.round(totalHundredths(entry, null) / 25);
  if (quarters <= 0) return { tapQuarters: 0, spares: 0 };
  const tapQuarters = ((quarters - 1) % 4) + 1;
  return { tapQuarters, spares: (quarters - tapQuarters) / 4 };
}

/** One plain sentence reading a keg back to the counter. */
export function kegReading(name: string, count: KegCount): string {
  const kegs = kegQuantity(count);
  const unit = kegs === "1" ? "keg" : "kegs";
  const tap =
    count.tapQuarters === 0
      ? "nothing left on tap"
      : count.tapQuarters === 4
        ? "a full keg on tap"
        : `${kegTapLabel(count.tapQuarters)} left on tap`;
  const spares =
    count.spares === 0
      ? ""
      : ` plus ${count.spares} full ${count.spares === 1 ? "spare" : "spares"}`;
  return `${name} reads ${kegs} ${unit}: ${tap}${spares}.`;
}

/** "Sixth barrel keg": the keg size value in words, then "keg". Null when the item has no keg size. */
export function kegSizeText(kegSize: string | null): string | null {
  if (kegSize === null || kegSize.trim().length === 0) return null;
  const words = kegSize.replace(/_/g, " ").trim().toLowerCase();
  const text = `${words.charAt(0).toUpperCase()}${words.slice(1)}`;
  return text.endsWith("keg") ? text : `${text} keg`;
}

export function isKegRow(row: Pick<TableRow, "containerType">): boolean {
  return row.containerType === "keg";
}
